/**
 * ProviderPricingTable — Tableau Ant Design des tarifs courants groupés
 * par provider. Utilisé sur la page liste Pricing.
 */

import { Button, Table, Tag, Tooltip } from 'antd';
import { History, Trash2 } from 'lucide-react';
import { useMemo, type FC } from 'react';
import type { PricingRow } from '@/pages/ops/pricing/types';
import { PROVIDER_LABELS, TIER_LABELS } from '@/pages/ops/pricing/types';

interface ProviderPricingTableProps {
  rows: PricingRow[];
  loading?: boolean;
  onShowHistory?: (row: PricingRow) => void;
  onSupersede?: (row: PricingRow) => void;
}

const TIER_COLORS: Record<string, string> = {
  any: 'default',
  free: 'default',
  starter: 'blue',
  team: 'purple',
  enterprise: 'gold',
};

function formatUsd(priceUsd: number): string {
  return priceUsd.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: priceUsd >= 1 ? 2 : 4,
    maximumFractionDigits: 6,
  });
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export const ProviderPricingTable: FC<ProviderPricingTableProps> = ({
  rows,
  loading,
  onShowHistory,
  onSupersede,
}) => {
  const dataSource = useMemo(() => rows, [rows]);

  return (
    <Table<PricingRow>
      rowKey="id"
      dataSource={dataSource}
      loading={loading}
      size="middle"
      pagination={{ pageSize: 20, showSizeChanger: false }}
      scroll={{ x: 1100 }}
    >
      <Table.Column<PricingRow>
        dataIndex="provider"
        title="Provider"
        width={220}
        sorter={(a, b) => a.provider.localeCompare(b.provider)}
        render={(p: string, record) => (
          <div className="flex flex-col">
            <span className="text-sm font-medium text-slate-900 dark:text-white">
              {record.providerLabel ?? PROVIDER_LABELS[record.provider]}
            </span>
            <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
              {p}
            </span>
          </div>
        )}
      />
      <Table.Column<PricingRow>
        dataIndex="tier"
        title="Tier"
        width={110}
        sorter={(a, b) => a.tier.localeCompare(b.tier)}
        render={(t: string, record) => (
          <Tag color={TIER_COLORS[t] ?? 'default'}>
            {record.tierLabel ?? TIER_LABELS[record.tier]}
          </Tag>
        )}
      />
      <Table.Column<PricingRow>
        dataIndex="unitTypeLabel"
        title="Unité"
        width={160}
        render={(label: string, record) => (
          <div className="flex flex-col">
            <span className="text-xs text-slate-700 dark:text-slate-300">{label}</span>
            <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
              {record.unitType}
            </span>
          </div>
        )}
      />
      <Table.Column<PricingRow>
        dataIndex="priceUsd"
        title="Prix"
        align="right"
        width={140}
        sorter={(a, b) => a.priceUsd - b.priceUsd}
        render={(usd: number, record) => (
          <div className="flex flex-col items-end">
            <span className="font-medium text-slate-900 dark:text-white">
              {formatUsd(usd)}
            </span>
            <span className="text-[10px] text-slate-400 dark:text-slate-500">
              {record.priceCents.toLocaleString('fr-FR')} cents
            </span>
          </div>
        )}
      />
      <Table.Column<PricingRow>
        dataIndex="effectiveFrom"
        title="Depuis"
        width={140}
        sorter={(a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom)}
        defaultSortOrder="descend"
        render={(iso: string) => (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {formatDate(iso)}
          </span>
        )}
      />
      <Table.Column<PricingRow>
        dataIndex="description"
        title="Description"
        width={220}
        render={(d: string | null) =>
          d ? (
            <Tooltip title={d}>
              <span className="truncate text-xs text-slate-600 dark:text-slate-300">
                {d}
              </span>
            </Tooltip>
          ) : (
            <span className="text-slate-400">—</span>
          )
        }
      />
      <Table.Column<PricingRow>
        title="Actions"
        width={160}
        fixed="right"
        render={(_, record) => (
          <div className="flex items-center gap-1">
            {onShowHistory && (
              <Tooltip title="Voir l'historique">
                <Button
                  type="text"
                  size="small"
                  icon={<History className="h-4 w-4" />}
                  onClick={() => onShowHistory(record)}
                />
              </Tooltip>
            )}
            {onSupersede && (
              <Tooltip title="Déprécier (clôturer)">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<Trash2 className="h-4 w-4" />}
                  onClick={() => onSupersede(record)}
                />
              </Tooltip>
            )}
          </div>
        )}
      />
    </Table>
  );
};

export default ProviderPricingTable;
